import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from '../../auth/staffAuthApi';
import { auth } from '../../auth/appAuth';
import NeatScheduleMark from '../../shared/components/brand/NeatScheduleMark';
import { sendCustomVerificationEmail } from '../../auth/api/emailVerificationApi';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Avatar, 
  Grid, 
  Link, 
  Paper,
  InputAdornment,
  IconButton,
  Slide,
  Divider
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { getDefaultDepositSettings } from '../../utils/deposits/initializeDepositSettings';
import { INITIAL_TUTORIAL_STATE } from '../tutorial/constants/initialTutorialState';
import { DEFAULT_BOOKING_CHECK_IN_SETTINGS } from '../settings/pages/booking/defaultBookingCheckInSettings';
import { alpha } from '@mui/material/styles';
import { useTheme } from '../../ThemeContext';
import AuthLoadingState from '../loading/AuthLoadingState';
import { BusinessPackage } from '../../packages/models/BusinessPackage';
import { provisionAccount } from '../../api/signup';

const Register = () => {
  const muiTheme = useMuiTheme();
  const { mode } = useTheme();
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingState, setLoadingState] = useState('initial');
  const navigate = useNavigate();

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setLoadingState('verifying');  // Start with verifying state
  
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }
  
    try {
      // Add a small delay to show the verifying state
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const now = new Date().toISOString();

      setLoadingState('loading');
      // Add a small delay to show the loading state
      await new Promise(resolve => setTimeout(resolve, 800));

      // Send custom verification email (SendGrid, no-reply@neatschedule.com)
      try {
        const idToken = await user.getIdToken();
        await sendCustomVerificationEmail(idToken);
      } catch (verifyErr) {
        console.warn('Could not send verification email:', verifyErr);
      }

      const companyId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `company_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

      setLoadingState('finalizing');
      // Add a small delay to show the finalizing state
      await new Promise(resolve => setTimeout(resolve, 800));

      // Create company on Postgres via API
      const companyData = {
        name: companyName,
        createdAt: now,
        updatedAt: now,
        ownerId: user.uid,
        ownerFirstName: firstName,
        ownerLastName: lastName,
        settings: {
          depositSettings: getDefaultDepositSettings(),
          bookingSettings: DEFAULT_BOOKING_CHECK_IN_SETTINGS,
          tutorial: INITIAL_TUTORIAL_STATE,
        },
        themeColor: '#1976d2',
        themeMode: 'light',
        package: {
          name: 'Business Package',
          limits: {
            employees: 5
          },
          createdAt: now,
          updatedAt: now
        },
        // Resource tracking
        resourceCounts: {
          employees: 1, // Starting with 1 since the owner is the first employee
          appointments: 0
        },
        // Individual counters for easier querying
        employeesCount: 1,
        appointmentsCount: 0
      };

      const userData = {
        email,
        firstName,
        lastName,
        companyId,
        companyName,
        role: 'owner',
        accountType: 'company', // Distinguish from client portal accounts
        emailVerified: false,
        createdAt: now,
        updatedAt: now,
        lastLogin: now,
        status: 'active',
        permissions: {
          CALENDAR: {
            VIEW: true,
            CREATE: true,
            EDIT: true,
            DELETE: true
          },
          APPOINTMENTS: {
            VIEW: true,
            CREATE: true,
            EDIT: true,
            DELETE: true
          },
          CLIENTS: {
            VIEW: true,
            CREATE: true,
            EDIT: true,
            DELETE: true
          },
          ARCHIVES: { VIEW: true },
          SETTINGS: {
            VIEW: true,
            EDIT: true
          }
        }
      };

      const idToken = await user.getIdToken();
      await provisionAccount({
        token: idToken,
        companyId,
        company: { id: companyId, ...companyData },
        user: userData,
      });

      console.log("Company and owner account created successfully");
      
      setLoadingState('success');
      // Add a small delay to show the success state
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Sign out and navigate
      await auth.signOut();
      navigate('/app/login', { 
        state: { 
          message: 'Account created successfully! Please check your email to verify your account before logging in.' 
        } 
      });
    } catch (error) {
      console.error("Registration error:", error);
      setError(error.message);
      setLoadingState('error');
      
      // Keep the error state visible for a moment
      await new Promise(resolve => setTimeout(resolve, 1500));
      setLoading(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: mode === 'dark' 
            ? '#121212'
            : '#ffffff',
          position: 'relative'
        }}
      >
        <AuthLoadingState 
          state={loadingState}
          onReset={() => {
            setLoading(false);
            setLoadingState('initial');
            setError('');
          }}
          customMessage={
            loadingState === 'verifying' ? 'Creating Your Account...' :
            loadingState === 'loading' ? 'Setting Up Your Company...' :
            loadingState === 'finalizing' ? 'Finalizing Setup...' :
            loadingState === 'success' ? 'Registration Successful!' :
            loadingState === 'error' ? 'Registration Failed' : 'Processing...'
          }
          customSubMessage={
            loadingState === 'verifying' ? 'Please wait while we create your account' :
            loadingState === 'loading' ? 'Setting up your company profile' :
            loadingState === 'finalizing' ? 'Setting up permissions and finalizing your account' :
            loadingState === 'success' ? 'Your account has been created successfully! You will be redirected to the login page.' :
            loadingState === 'error' ? (error || "There was a problem creating your account") : 'Please wait...'
          }
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: mode === 'dark' 
          ? '#121212'
          : '#ffffff',
        position: 'relative'
      }}
    >
      {/* Main content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: '100vh',
          p: 2
        }}
      >
        {/* Company Logo */}
        <Slide direction="down" in={true} timeout={{ enter: 700, exit: 500 }} easing={{ enter: 'cubic-bezier(0.34, 1.56, 0.64, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Box
            sx={{
              mb: 4,
              filter: mode === 'dark' ? 'drop-shadow(0 4px 20px rgba(255, 255, 255, 0.15))' : 'drop-shadow(0 4px 20px rgba(0, 0, 0, 0.15))'
            }}
          >
            <NeatScheduleMark variant={mode === 'dark' ? 'dark' : 'light'} />
          </Box>
        </Slide>
        
        {/* Subtitle */}
        <Slide direction="down" in={true} timeout={{ enter: 900, exit: 500 }} easing={{ enter: 'cubic-bezier(0.34, 1.56, 0.64, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 4,
              color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
              fontWeight: 500,
              textAlign: 'center'
            }}
          >
            Create your account
          </Typography>
        </Slide>
        
        {/* Registration Card */}
        <Slide direction="up" in={true} timeout={{ enter: 1000, exit: 500 }} easing={{ enter: 'cubic-bezier(0.34, 1.56, 0.64, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: 600,
              borderRadius: 4,
              overflow: 'hidden',
              backgroundColor: mode === 'dark' ? '#1E1E1E' : '#ffffff',
              boxShadow: mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                : '0 8px 32px rgba(0, 0, 0, 0.1)',
              p: 3
            }}
          >
            {/* Error Alert */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  width: '100%', 
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: mode === 'dark' ? '#ff6b6b' : undefined
                  }
                }}
              >
                {error}
              </Alert>
            )}
            
            <form onSubmit={handleRegister} style={{ width: '100%' }}>
              <Grid container spacing={2}>
                {/* Company Name */}
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="companyName"
                    placeholder="Company Name"
                    name="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          borderColor: 'transparent'
                        },
                        '&:hover fieldset': {
                          borderColor: 'transparent'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                          borderWidth: '1px',
                        }
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 14px 16px 0',
                      }
                    }}
                  />
                </Grid>
                
                {/* First Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="firstName"
                    placeholder="First Name"
                    name="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          borderColor: 'transparent'
                        },
                        '&:hover fieldset': {
                          borderColor: 'transparent'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                          borderWidth: '1px',
                        }
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 14px 16px 0',
                      }
                    }}
                  />
                </Grid>
                
                {/* Last Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="lastName"
                    placeholder="Last Name"
                    name="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          borderColor: 'transparent'
                        },
                        '&:hover fieldset': {
                          borderColor: 'transparent'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                          borderWidth: '1px',
                        }
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 14px 16px 0',
                      }
                    }}
                  />
                </Grid>
                
                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="email"
                    placeholder="Email Address"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          borderColor: 'transparent'
                        },
                        '&:hover fieldset': {
                          borderColor: 'transparent'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                          borderWidth: '1px',
                        }
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 14px 16px 0',
                      }
                    }}
                  />
                </Grid>
                
                {/* Password */}
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    placeholder="Password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          borderColor: 'transparent'
                        },
                        '&:hover fieldset': {
                          borderColor: 'transparent'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                          borderWidth: '1px',
                        }
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 14px 16px 0',
                      }
                    }}
                  />
                </Grid>
                
                {/* Confirm Password */}
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={handleToggleConfirmPasswordVisibility}
                            edge="end"
                            size="small"
                          >
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          borderColor: 'transparent'
                        },
                        '&:hover fieldset': {
                          borderColor: 'transparent'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: muiTheme.palette.primary.main,
                          borderWidth: '1px',
                        }
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 14px 16px 0',
                      }
                    }}
                  />
                </Grid>
              </Grid>
              
              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                Create Account
              </Button>
              
              {/* Login Link */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/app/login"
                  sx={{
                    textTransform: 'none',
                    color: muiTheme.palette.primary.main,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Already have an account? Sign in
                </Button>
              </Box>
            </form>
          </Box>
        </Slide>
      </Box>
    </Box>
  );
};

export default Register;
